/**
 * §A.5 · Los 28 procesos programados.
 *
 * ## Lo que esta pantalla existe para contestar
 *
 * **«¿Qué no ha corrido?»**, y no «¿qué ha fallado?». Son distintas, y la primera
 * es la que se olvida: un proceso que falla escribe un error y alguien lo ve; un
 * proceso que **no se ejecuta no genera nada**. Silencio absoluto. Por eso
 * `overdue` va arriba y en ámbar, por delante de los fallos.
 *
 * El servidor lo resuelve cruzando **el catálogo declarado** con `job_runs`, no
 * listando las filas que hay: si la lista saliera de las ejecuciones existentes, un
 * proceso que lleva tres semanas muerto sencillamente no aparecería. Esa es toda la
 * gracia del endpoint y por eso aquí no se filtra nada.
 *
 * ## `slow` es contra la mediana, no contra la media
 *
 * Una vuelta lenta porque el proveedor estaba caído se lleva la media por delante y
 * deja de detectar el proceso que de verdad se está degradando. Lo calcula el
 * servidor; aquí solo se pinta.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  claseDeProceso,
  duracion,
  etiquetaDeProceso,
  type EjecucionDeProceso,
  type Proceso,
} from '@/backoffice/tipos';

export function Procesos() {
  const [abierto, setAbierto] = useState<string | null>(null);

  const procesos = useQuery({
    queryKey: ['admin', 'procesos'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.jobs.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Proceso[];
    },
  });

  const vueltas = useQuery({
    queryKey: ['admin', 'vueltas', abierto],
    enabled: Boolean(abierto),
    queryFn: async () => {
      const { data, error } = await api.v1.admin
        .jobs({ name: abierto! })
        .runs.get({ query: { limit: 25 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as EjecucionDeProceso[];
    },
  });

  if (procesos.isPending) {
    return <Cargando filas={8} etiqueta="Cargando los procesos" />;
  }

  if (procesos.isError || !procesos.data) {
    return (
      <PanelError
        problema={aProblema(procesos.error)}
        reintentar={() => void procesos.refetch()}
      />
    );
  }

  /*
   * **Atrasado y «nunca ha corrido» no son lo mismo**, aunque el servidor mande
   * los dos a la vez. Uno es un proceso que iba bien y ha dejado de ir; el otro es
   * una instalación donde el planificador todavía no se ha puesto en marcha.
   *
   * Se separan porque si no, en desarrollo salen los 28 en ámbar y el aviso escupe
   * 28 nombres en un párrafo. Un muro de alertas es un muro que nadie lee — que es
   * lo contrario de lo que esta pantalla existe para hacer.
   */
  const nuncaHanCorrido = procesos.data.filter((p) => p.never_run);
  const atrasados = procesos.data.filter((p) => p.overdue && !p.never_run);
  const fallando = procesos.data.filter((p) => p.failed_last_24h > 0);
  const lentos = procesos.data.filter((p) => p.slow);
  /*
   * Si no ha corrido **ninguno**, el problema no son 28 procesos: es uno solo, el
   * planificador. Decirlo así es la diferencia entre una lista que asusta y una
   * frase que se puede accionar.
   */
  const planificadorParado = nuncaHanCorrido.length === procesos.data.length;

  /** Unos pocos nombres y «y N más»: veintiocho en un párrafo no se leen. */
  const nombres = (lista: Proceso[], cuantos = 4): string => {
    const primeros = lista.slice(0, cuantos).map((p) => p.name);
    const resto = lista.length - primeros.length;
    return resto > 0
      ? `${primeros.join(', ')} y ${resto} más`
      : primeros.join(', ');
  };

  // ── El histórico de uno ──────────────────────────────────────────────────
  if (abierto) {
    return (
      <section>
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => setAbierto(null)}
          >
            ← Procesos
          </button>
        </div>

        <h1>
          <code>{abierto}</code>
        </h1>

        {vueltas.isPending && <Cargando filas={5} etiqueta="Cargando" />}

        {vueltas.isError && (
          <PanelError
            problema={aProblema(vueltas.error)}
            reintentar={() => void vueltas.refetch()}
          />
        )}

        {vueltas.data?.length === 0 && (
          <p className="tenue">
            No ha corrido nunca. Si su cadencia dice que debería, eso es lo que
            hay que mirar: un proceso que no arranca no deja error.
          </p>
        )}

        {vueltas.data && vueltas.data.length > 0 && (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Club</th>
                  <th>Estado</th>
                  <th className="num">Tardó</th>
                  <th className="num">Hizo</th>
                  <th>Error</th>
                </tr>
              </thead>
              {/* Los fallidos arriba: lo dice el contrato y es lo que se viene a ver. */}
              <tbody>
                {vueltas.data.map((v) => (
                  <tr
                    key={v.id}
                    className={v.status === 'failed' ? 'fila-aviso' : undefined}
                  >
                    <td className="tenue">{fechaYHora(v.started_at)}</td>
                    <td className="tenue">
                      {v.academy_name ?? (
                        <span title={v.scope_key}>toda la plataforma</span>
                      )}
                    </td>
                    <td>
                      <span className={`pastilla ${claseDeProceso(v.status)}`}>
                        {etiquetaDeProceso(v.status)}
                      </span>
                    </td>
                    <td className="num tenue">{duracion(v.duration_ms)}</td>
                    <td className="num tenue">
                      {v.items_processed}
                      {v.items_failed > 0 && (
                        <strong> · {v.items_failed} mal</strong>
                      )}
                    </td>
                    <td className="tenue">{v.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  }

  // ── Los 28 ───────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Procesos programados</h1>
      </header>

      {/*
        El aviso que importa va primero y es el de los atrasados. Un proceso caído
        grita; uno que no arranca, no.
      */}
      {planificadorParado ? (
        <div className="aviso" role="status">
          <strong>
            Ninguno de los {procesos.data.length} ha corrido nunca.
          </strong>{' '}
          Eso no son {procesos.data.length} problemas, es uno: el planificador
          no está en marcha. En una máquina de desarrollo es lo normal.
        </div>
      ) : atrasados.length > 0 ? (
        <div className="aviso" role="status">
          <strong>
            {atrasados.length === 1
              ? '1 proceso lleva más de lo que debería sin correr'
              : `${atrasados.length} procesos llevan más de lo que deberían sin correr`}
            :
          </strong>{' '}
          {nombres(atrasados)}.
          <p className="tenue">
            Esto no genera ningún error por sí solo, y por eso se pregunta
            expresamente.
          </p>
        </div>
      ) : (
        <div className="aviso aviso-calma" role="status">
          <strong>Todos han corrido cuando tocaba.</strong>{' '}
          {fallando.length === 0
            ? 'Y ninguno ha fallado en 24 horas.'
            : `Pero ${fallando.length} han fallado en las últimas 24 horas.`}
          {nuncaHanCorrido.length > 0 && (
            <p className="tenue">
              {nuncaHanCorrido.length === 1
                ? 'Uno no ha corrido nunca'
                : `${nuncaHanCorrido.length} no han corrido nunca`}
              : {nombres(nuncaHanCorrido)}.
            </p>
          )}
        </div>
      )}

      {lentos.length > 0 && (
        <p className="tenue">
          Y{' '}
          {lentos.length === 1 ? 'uno va lento' : `${lentos.length} van lentos`}
          : tardaron más del triple que su mediana. Puede ser el día o puede ser
          que se estén degradando.
        </p>
      )}

      <div className="tabla-marco">
        <table className="tabla">
          <thead>
            <tr>
              <th>Proceso</th>
              <th>Cuándo</th>
              <th>Última vuelta</th>
              <th>Estado</th>
              <th className="num">Tardó</th>
              <th className="num">24 h</th>
            </tr>
          </thead>
          <tbody>
            {procesos.data.map((p) => (
              <tr
                key={p.name}
                className={
                  (p.overdue && !p.never_run) || p.failed_last_24h > 0
                    ? 'fila-aviso'
                    : undefined
                }
              >
                <td>
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => setAbierto(p.name)}
                  >
                    <code>{p.name}</code>
                  </button>
                  {p.overdue && !p.never_run && (
                    <span className="pastilla pastilla-paused pastilla-left">
                      Atrasado
                    </span>
                  )}
                  {p.slow && (
                    <span className="pastilla pastilla-paused pastilla-left">
                      Lento
                    </span>
                  )}
                </td>
                <td className="tenue">{p.schedule}</td>
                <td className="tenue">
                  {p.never_run ? 'Nunca' : fechaYHora(p.last_run_at)}
                </td>
                <td>
                  <span className={`pastilla ${claseDeProceso(p.last_status)}`}>
                    {etiquetaDeProceso(p.last_status)}
                  </span>
                </td>
                <td className="num tenue">{duracion(p.last_duration_ms)}</td>
                <td className="num tenue">
                  {p.runs_last_24h}
                  {p.failed_last_24h > 0 && (
                    <strong> · {p.failed_last_24h} mal</strong>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="tenue">
        La lista sale del <strong>catálogo declarado</strong> y no de las
        ejecuciones que hay: un proceso que lleva tres semanas muerto tiene que
        aparecer, y si saliera de sus filas no aparecería.
      </p>
    </section>
  );
}
